/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { AssetMainType, IAssetInfo } from "../../models/asset";
import { useFormik } from "formik";
import { AssetSummary } from "../../commons/assetSummary";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import { applyTreasureFieldEdit, freshTreasureLink, TreasureDraft } from "../../commons/treasure-field-link";
import AssetSearchSelector from "../AssetSearchSelector";
import View from "../Container";
import Text from "../Text";
import Label from "../Label";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import Button from "../Button";
import Container from "../Container";
import Show from "../Show";
import { useHomeContext } from "../../contexts/HomeContext";
import { useExchangeRateByType } from "../../hooks/useExchangeRateByType";
import { useTreasureUnitValue } from "../../hooks/useTreasureUnitValue";
import {
    assetSummaryTypeToAnalytics,
    trackChargeCompleted,
    trackChargeFailed,
    trackChargeSubmitted,
    Tracker,
} from "../../analytics";

interface IChargeForm {
    selectedAsset?: AssetSummary;
    date?: Date;
    cotation?: number;
    aportValue?: number;
    quantity?: number | null;
    unitValue?: number | null;
}

/** Tesouro (exceto Reserva) é marcado por títulos × PU: o aporte precisa do PU para virar quantidade. */
const isTreasureByQuantity = (asset?: AssetSummary) =>
    asset?.type === AssetMainType.TreasureBound && !!asset.treasureId && !asset.isTesouroReserva;

interface IAportHandleFormProps {
    onSuccess: () => void;
}

const AportHandleForm = ({ onSuccess }: IAportHandleFormProps) => {
    const http = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const chargeMutation = useMutation({
        mutationFn: async (form: IChargeForm) => {
            const aportValue = form.selectedAsset?.type === AssetMainType.Cripto
                    || form.selectedAsset?.type === AssetMainType.Currency
                    || form.selectedAsset?.type === AssetMainType.InvestimentFunds
                    || form.selectedAsset?.type === AssetMainType.Stock
                ? (form.cotation ?? 0) * (form.quantity ?? 0)
                : form.aportValue;

            const body = {
                event_date: moment(form.date).format("YYYY-MM-DD HH:mm:ss"),
                event_value: aportValue,
                event_quantity: form.quantity,
                ...(isTreasureByQuantity(form.selectedAsset) ? { event_unit_value: form.unitValue } : {}),
            };

            return await http.doPatch<any, IAssetInfo>(`asset/${form.selectedAsset?.id}/charge`, body);
        }
    });

    const formik = useFormik<IChargeForm>({
        initialValues: {
            selectedAsset: undefined,
            date: undefined,
            cotation: undefined,
            quantity: undefined,
            aportValue: undefined,
            unitValue: undefined
        },
        onSubmit: async (values) => {
            await send(values);
        },
        validationSchema: () => Yup
            .object()
            .shape({
                selectedAsset: Yup.object().required("Campo obrigatório"),
                date: Yup.date().required("Campo obrigatório"),
                quantity: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (
                                selectedAsset?.type === AssetMainType.Cripto
                                || selectedAsset?.type === AssetMainType.Currency
                                || selectedAsset?.type === AssetMainType.InvestimentFunds
                                || selectedAsset?.type === AssetMainType.Stock
                            ) {
                                return schema.required("Campo obrigatório").min(0.0001, "Valor deve ser maior que 0");
                            }


                            return schema;
                        }
                    )
                    .nullable(),
                cotation: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (
                                selectedAsset?.type === AssetMainType.Cripto
                                || selectedAsset?.type === AssetMainType.Currency
                                || selectedAsset?.type === AssetMainType.InvestimentFunds
                                || selectedAsset?.type === AssetMainType.Stock
                            ) {
                                return schema.required("Campo obrigatório").min(0.0001, "Valor deve ser maior que 0");
                            }


                            return schema;
                        }
                    )
                    .nullable(),
                aportValue: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (
                                selectedAsset?.type !== AssetMainType.Cripto
                                && selectedAsset?.type !== AssetMainType.Currency
                                && selectedAsset?.type !== AssetMainType.InvestimentFunds
                                && selectedAsset?.type !== AssetMainType.Stock
                            ) {
                                return schema.required("Campo obrigatório").min(0.0001, "Valor deve ser maior que 0");
                            }
                            return schema;
                        }
                    )
                    .nullable(),
                unitValue: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => isTreasureByQuantity(selectedAsset)
                            ? schema.required("Informe o preço unitário (PU)").moreThan(0, "Valor deve ser maior que 0")
                            : schema
                    )
                    .nullable()
            })
    });

    const treasureLink = useRef(freshTreasureLink());
    const treasureDraft = useRef({ value: 0, quantity: 0, unit: 0 });
    const echoedQuantity = useRef<number | null>(null);
    const echoedAmount = useRef<number | null>(null);
    treasureDraft.current = {
        value: Number(formik.values.aportValue ?? 0),
        quantity: Number(formik.values.quantity ?? 0),
        unit: Number(formik.values.unitValue ?? 0)
    };

    function editTreasure(field: "value" | "quantity" | "unit", patch: Partial<TreasureDraft>) {
        if (!isTreasureByQuantity(formik.values.selectedAsset)) {
            return;
        }
        const draft: TreasureDraft = { ...treasureDraft.current, ...patch };
        const result = applyTreasureFieldEdit(treasureLink.current, field, draft);
        treasureLink.current = result.link;
        if (patch.value != null) {
            treasureDraft.current.value = patch.value;
        }
        if (patch.unit != null) {
            treasureDraft.current.unit = patch.unit;
        }
        if (result.quantity != null) {
            const typed = patch.quantity;
            const changed = typed == null || Math.abs(result.quantity - typed) > 1e-9;
            treasureDraft.current.quantity = result.quantity;
            if (changed) {
                echoedQuantity.current = result.quantity;
                formik.setFieldValue("quantity", result.quantity);
            }
        }
        if (result.value != null) {
            treasureDraft.current.value = result.value;
            echoedAmount.current = result.value;
            formik.setFieldValue("aportValue", result.value);
        }
    }

    const treasureUnitValueQuery = useTreasureUnitValue(
        isTreasureByQuantity(formik.values.selectedAsset) ? formik.values.selectedAsset?.treasureId : undefined,
        formik.values.date,
        "purchase",
        (unitValue) => {
            formik.setFieldValue("unitValue", unitValue);
            editTreasure("unit", { unit: unitValue ?? 0 });
        }
    );

    const { exchangeRateQuery } = useExchangeRateByType(
        formik.values.selectedAsset?.type?.toLowerCase() as AssetMainType, 
        formik.values.date, 
        formik.values.selectedAsset?.name.split("-")[0],
        (cotation: number) => {
            formik.setFieldValue("cotation", cotation);
        },
        undefined,
        formik.values.selectedAsset?.fundType
    );

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const {
        isPre,
        indexCorrelationName,
        indexCorrelation,
        issuingBankName,
        negotiatingBankName,
        dueDate,
        purchaseDate,
        brokerageValue,
        othersValue,
        monthlyCosts,
        quantity,
        yearTax,
        cotation,
        recurringValue,
        pbType
    } = formik.values.selectedAsset ?? {};

    const toCurrency = (value: number) => {
        return Intl.NumberFormat(
            "pt-BR",
            {
                style: "currency",
                currency: "BRL"
            }
        )
            .format(value);
    }

    const infos: { label: string; value: string; }[] = [];
    if (pbType) {
        infos.push({
            label: "Ativo",
            value: `${pbType} ${isPre ? "pré-fixado" : "pós-fixado"}`
        });
    }

    if (indexCorrelation) {
        infos.push({
            label: "Taxa",
            value: `${indexCorrelationName} + ${indexCorrelation}%`
        });
    }

    if (dueDate) {
        infos.push({
            label: "Vencimento",
            value: moment(dueDate).format("DD/MM/YYYY")
        });
    }

    if (purchaseDate) {
        infos.push({
            label: "Compra",
            value: moment(purchaseDate).format("DD/MM/YYYY")
        });
    }

    if (issuingBankName) {
        infos.push({
            label: "Emissor",
            value: issuingBankName
        });
    }

    if (negotiatingBankName) {
        infos.push({
            label: "Corretora",
            value: negotiatingBankName
        });
    }

    if (brokerageValue || othersValue) {
        infos.push({
            label: "Custos Operacionais",
            value: toCurrency((brokerageValue ?? 0) + (othersValue ?? 0))
        });
    }

    if (monthlyCosts) {
        infos.push({
            label: "Custos Mensais",
            value: toCurrency((monthlyCosts ?? 0))
        });
    }

    if (quantity) {
        infos.push({
            label: "Quantidade",
            value: quantity.toString()
        });
    }

    if (yearTax) {
        infos.push({
            label: "Taxa anual",
            value: `${yearTax}%`
        });
    }

    if (recurringValue) {
        infos.push({
            label: "Valor recorrente",
            value: toCurrency((recurringValue ?? 0))
        });
    }

    if (cotation) {
        infos.push({
            label: "Cotação",
            value: toCurrency((cotation ?? 0))
        });
    }

    const send = async (form: IChargeForm) => {
        const assetType = assetSummaryTypeToAnalytics(form.selectedAsset?.type);
        if (assetType) trackChargeSubmitted(assetType);
        try {
            const res = await chargeMutation.mutateAsync(form);

            if ((res?.status ?? 0) > 299 || (res?.status ?? 0) <= 0) {
                throw new Error("Falha ao cadastrar aporte");
            }

            if (assetType) trackChargeCompleted(assetType);
            onSuccess();
            await homeContext.refresh();
        } catch (err) {
            if (assetType) trackChargeFailed(assetType);
            Tracker.trackError('AportHandleForm', 'validation');
            toast.current?.show({
                severity: "error",
                summary: "Falha ao cadastrar aporte",
                detail: isTreasureByQuantity(form.selectedAsset) && err instanceof Error ? err.message : undefined
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />

            <AssetSearchSelector
                label="Informe o ativo"
                hasError={!!formik.errors.selectedAsset && formik.touched.selectedAsset}
                error={formik.errors.selectedAsset}
                selected={formik.values.selectedAsset}
                onSelectBank={(bank: AssetSummary | null) => {
                    if (bank?.id !== formik.values.selectedAsset?.id) {
                        formik.setFieldValue("quantity", undefined);
                        formik.setFieldValue("unitValue", undefined);
                        treasureLink.current = freshTreasureLink();
                    }
                    formik.setFieldValue("selectedAsset", bank);
                }}
            />

            <FormField
                id="date"
                label="Data do aporte"
                hasError={!!formik.errors.date && formik.touched.date}
                error={formik.errors.date}
            >
                <Calendar
                    invalid={!!formik.errors.date && formik.touched.date}
                    value={formik.values.date}
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                    showIcon
                    disabledDays={[0, 6]}
                    maxDate={new Date()}
                    icon={exchangeRateQuery.isLoading ? "pi pi-spin pi-spinner" : "pi pi-calendar"}
                    onChange={(event) => {
                        formik.setFieldValue("date", event.value)
                    }}
                    locale="pt"
                />
            </FormField>

            <Show when={
                formik.values.selectedAsset?.type === AssetMainType.Cripto
                || formik.values.selectedAsset?.type === AssetMainType.Currency
                || formik.values.selectedAsset?.type === AssetMainType.InvestimentFunds
                || formik.values.selectedAsset?.type === AssetMainType.Stock
            }>
                <FormField
                    id="quantity"
                    hasError={!!formik.errors.quantity && formik.touched.quantity}
                    error={formik.errors.quantity}
                    label="Quantidade"
                >
                    <InputNumber
                        invalid={!!formik.errors.quantity && formik.touched.quantity}
                        value={formik.values.quantity}
                        minFractionDigits={2}
                        maxFractionDigits={10}
                        onValueChange={(e) =>
                            formik.setFieldValue("quantity", e.value)
                        }
                        locale="pt-BR"
                    />
                </FormField>
            </Show>

            <Show when={
                formik.values.selectedAsset?.type === AssetMainType.Cripto
                || formik.values.selectedAsset?.type === AssetMainType.Currency
                || formik.values.selectedAsset?.type === AssetMainType.InvestimentFunds
                || formik.values.selectedAsset?.type === AssetMainType.Stock
            }>
                <FormField
                    id="cotation"
                    hasError={!!formik.errors.cotation && formik.touched.cotation}
                    error={formik.errors.cotation}
                    label="Cotação"
                >
                    <InputNumber
                        invalid={!!formik.errors.cotation && formik.touched.cotation}
                        value={formik.values.cotation}
                        onValueChange={(e) =>
                            formik.setFieldValue("cotation", e.value)
                        }
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                    />
                </FormField>
            </Show>
            
            <Show when={
                formik.values.selectedAsset?.type === AssetMainType.Cripto
                || formik.values.selectedAsset?.type === AssetMainType.Currency
                || formik.values.selectedAsset?.type === AssetMainType.InvestimentFunds
                || formik.values.selectedAsset?.type === AssetMainType.Stock
            }>
                <Container flexColumn gap={2}>
                    <Label>
                        Valor total do aporte
                    </Label>

                    <InputNumber
                        disabled
                        value={(formik.values.cotation ?? 0) * (formik.values.quantity ?? 0)}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                    />
                </Container>
            </Show>

            <Show when={
                formik.values.selectedAsset?.type !== AssetMainType.Cripto
                && formik.values.selectedAsset?.type !== AssetMainType.Currency
                && formik.values.selectedAsset?.type !== AssetMainType.InvestimentFunds
                && formik.values.selectedAsset?.type !== AssetMainType.Stock
            }>
                <FormField
                    id="aportValue"
                    hasError={!!formik.errors.aportValue && formik.touched.aportValue}
                    error={formik.errors.aportValue}
                    label="Valor total do aporte"
                >
                    <InputNumber
                        invalid={!!formik.errors.aportValue && formik.touched.aportValue}
                        onValueChange={(e) => {
                        const next = e.value ?? 0;
                        if (echoedAmount.current != null) {
                            const echo = echoedAmount.current;
                            echoedAmount.current = null;
                            if (Math.abs(next - echo) < 0.009) {
                                return;
                            }
                        }
                        formik.setFieldValue("aportValue", next);
                        editTreasure("value", { value: next });
                        }}
                        value={formik.values.aportValue}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                    />
                </FormField>
            </Show>

            <Show when={isTreasureByQuantity(formik.values.selectedAsset)}>
                <FormField
                    id="unitValue"
                    hasError={!!formik.errors.unitValue && formik.touched.unitValue}
                    error={formik.errors.unitValue}
                    label="Preço unitário (PU) da compra"
                >
                    <InputNumber
                        invalid={!!formik.errors.unitValue && formik.touched.unitValue}
                        value={formik.values.unitValue}
                        onValueChange={(e) => {
                            formik.setFieldValue("unitValue", e.value);
                            editTreasure("unit", { unit: e.value ?? 0 });
                        }}
                        disabled={treasureUnitValueQuery.isFetching}
                        mode="currency"
                        currency="BRL"
                        minFractionDigits={2}
                        maxFractionDigits={8}
                        locale="pt-BR"
                    />
                </FormField>

                <FormField
                    id="treasureQuantity"
                    hasError={!!formik.errors.quantity && formik.touched.quantity}
                    error={formik.errors.quantity}
                    label="Quantidade de títulos"
                >
                    <InputNumber
                        value={formik.values.quantity}
                        minFractionDigits={2}
                        maxFractionDigits={6}
                        onValueChange={(e) => {
                            const next = e.value ?? 0;
                            if (echoedQuantity.current != null) {
                                const echo = echoedQuantity.current;
                                echoedQuantity.current = null;
                                if (Math.abs(next - echo) < 1e-9) {
                                    return;
                                }
                            }
                            formik.setFieldValue("quantity", next);
                            editTreasure("quantity", { quantity: next });
                        }}
                        locale="pt-BR"
                    />
                </FormField>

                <Text size="xs" className="text-gray-500">
                    Sugerimos o PU de compra do dia. O valor calcula a quantidade uma vez (valor ÷ PU). Depois disso, valor e quantidade ficam independentes: informe os números da nota, mesmo que o valor não feche quantidade × PU.
                </Text>
            </Show>

            {
                !!infos.length && <View flexColumn gap={2}>
                    <Label className="border-bottom-1 border-gray-300 pb-2">
                        Dados do Investimento
                    </Label>

                    <View className="flex flex-column items-between gap-3">
                        {
                            infos.map(info => {
                                return (
                                    <View key={info.label} className="flex flex-column flex-row justify-between">
                                        <Label>{info.label}</Label>

                                        <Text className="text-xs max-w-xs">{info.value}</Text>
                                    </View>
                                )
                            })
                        }
                    </View>
                </View>
            }

            <View fullWidth>
                <Button
                    onClick={() => {
                        console.log(formik.errors);
                        formik.handleSubmit();
                        return Promise.resolve();
                    }}
                    className="w-full justify-content-center"
                    loading={chargeMutation.isPending}
                    disabled={chargeMutation.isPending}
                >
                    Cadastrar
                </Button>
            </View>
        </>
    )
}

export default AportHandleForm;
